const BREWFATHER_API_BASE = 'https://api.brewfather.app/v2';

export type BrewfatherBatch = {
	_id: string;
	name: string;
	batchNo?: number;
	recipe?: {
		_id: string;
		name: string;
	};
	status: 'Planning' | 'Brewing' | 'Fermenting' | 'Conditioning' | 'Completed' | 'Archived';
	brewDate?: number | string;
	bottlingDate?: number | string;
	measuredOg?: number;
	measuredFg?: number;
	measuredAbv?: number;
	measuredIbu?: number;
	measuredColor?: number;
	notes?: string | Array<{
		timestamp: number;
		note: string;
		status?: string;
		type?: string;
	}>;
	brewer?: string;
};

export type BrewfatherRecipe = {
	_id: string;
	name: string;
	author?: string;
	style?: string | { name: string; [key: string]: any } | null;
	type: 'All Grain' | 'Extract' | 'Partial Mash' | 'BIAB';
	og?: number;
	fg?: number;
	abv?: number;
	ibu?: number;
	color?: number;
	description?: string;
	fermentables?: Array<{
		name: string;
		amount: number;
		unit: string;
	}>;
	hops?: Array<{
		name: string;
		amount: number;
		unit: string;
		time: number;
		use: string;
	}>;
	yeast?: Array<{
		name: string;
		amount?: number;
		unit?: string;
	}>;
	other?: Array<{
		name: string;
		amount: number;
		unit: string;
		use: string;
	}>;
	mash?: Array<{
		name: string;
		temp: number;
		time: number;
	}>;
};

export type BrewfatherBatchReading = {
	battery?: number;
	id?: string;
	rssi?: number;
	temp?: number;
	type?: string;
	sg?: number;
	time?: number;
	angle?: number;
};

import { IntegrationService } from './IntegrationService';

// Cache for credentials to avoid repeated API calls
let credentialsCache: { apiKey: string; userId: string } | null = null;
let cacheUserId: string | null = null;

export const getBrewfatherCredentials = async (
	userId: string,
	authHeader: Record<string, string>
): Promise<{ apiKey: string; userId: string } | null> => {
	// Return cached if available and for same user
	if (credentialsCache && cacheUserId === userId) {
		return credentialsCache;
	}

	try {
		const response = await IntegrationService.get(userId, 'brewfather', authHeader);
		if (response.item?.credentials) {
			const creds = response.item.credentials as { userId: string; apiKey: string };
			// Cache the credentials
			credentialsCache = {
				apiKey: creds.apiKey,
				userId: creds.userId
			};
			cacheUserId = userId;
			return credentialsCache;
		}
		return null;
	} catch (error: any) {
		// Integration not found - return null
		if (error.message?.includes('404') || error.message?.includes('not found')) {
			return null;
		}
		throw error;
	}
};

export const setBrewfatherCredentials = async (
	userId: string,
	apiKey: string,
	brewfatherUserId: string,
	authHeader: Record<string, string>
): Promise<void> => {
	await IntegrationService.createOrUpdate(
		userId,
		'brewfather',
		{
			userId: brewfatherUserId,
			apiKey: apiKey
		},
		authHeader
	);
	// Clear cache after update
	credentialsCache = null;
	cacheUserId = null;
};

export const clearBrewfatherCredentials = async (
	userId: string,
	authHeader: Record<string, string>
): Promise<void> => {
	await IntegrationService.delete(userId, 'brewfather', authHeader);
	// Clear cache
	credentialsCache = null;
	cacheUserId = null;
};

const createAuthHeader = (userId: string, apiKey: string): string => {
	return `Basic ${btoa(`${userId}:${apiKey}`)}`;
};

const fetchAllPages = async <T>(
	endpoint: string,
	authHeader: string,
	limit: number = 50
): Promise<T[]> => {
	const allItems: T[] = [];
	let startAfter: string | undefined = undefined;
	let hasMore = true;

	while (hasMore) {
		const params = new URLSearchParams({
			limit: limit.toString(),
			complete: 'True'
		});
		if (startAfter) {
			params.append('start_after', startAfter);
		}

		const response = await fetch(`${BREWFATHER_API_BASE}${endpoint}?${params.toString()}`, {
			method: 'GET',
			headers: {
				'Authorization': authHeader
			}
		});

		if (!response.ok) {
			if (response.status === 429) {
				const retryAfter = response.headers.get('Retry-After');
				throw new Error(`Rate limit exceeded. Retry after ${retryAfter} seconds`);
			}
			if (response.status === 401) {
				throw new Error('Invalid Brewfather credentials. Please check your API key and User ID.');
			}
			throw new Error(`Brewfather API error: ${response.status} ${response.statusText}`);
		}

		const data = await response.json();
		const items = Array.isArray(data) ? data : [];
		
		if (items.length === 0) {
			hasMore = false;
		} else {
			allItems.push(...items);
			startAfter = items[items.length - 1]._id;
			hasMore = items.length === limit;
		}
	}

	return allItems;
};

export const BrewfatherService = {
	getBatches: async (userId: string, authHeader: Record<string, string>): Promise<BrewfatherBatch[]> => {
		const credentials = await getBrewfatherCredentials(userId, authHeader);
		if (!credentials) {
			throw new Error('Brewfather credentials not configured. Please set your API key and User ID in settings.');
		}
		const brewfatherAuthHeader = createAuthHeader(credentials.userId, credentials.apiKey);
		return fetchAllPages<BrewfatherBatch>('/batches', brewfatherAuthHeader);
	},

	getRecipes: async (userId: string, authHeader: Record<string, string>): Promise<BrewfatherRecipe[]> => {
		const credentials = await getBrewfatherCredentials(userId, authHeader);
		if (!credentials) {
			throw new Error('Brewfather credentials not configured. Please set your API key and User ID in settings.');
		}
		const brewfatherAuthHeader = createAuthHeader(credentials.userId, credentials.apiKey);
		return fetchAllPages<BrewfatherRecipe>('/recipes', brewfatherAuthHeader);
	},

	getBatch: async (batchId: string, userId: string, authHeader: Record<string, string>): Promise<BrewfatherBatch> => {
		const credentials = await getBrewfatherCredentials(userId, authHeader);
		if (!credentials) {
			throw new Error('Brewfather credentials not configured.');
		}
		const brewfatherAuthHeader = createAuthHeader(credentials.userId, credentials.apiKey);
		const response = await fetch(`${BREWFATHER_API_BASE}/batches/${batchId}`, {
			method: 'GET',
			headers: {
				'Authorization': brewfatherAuthHeader
			}
		});
		if (!response.ok) {
			if (response.status === 401) {
				throw new Error('Invalid Brewfather credentials.');
			}
			throw new Error(`Brewfather API error: ${response.status}`);
		}
		return await response.json();
	},

	getRecipe: async (recipeId: string, userId: string, authHeader: Record<string, string>): Promise<BrewfatherRecipe> => {
		const credentials = await getBrewfatherCredentials(userId, authHeader);
		if (!credentials) {
			throw new Error('Brewfather credentials not configured.');
		}
		const brewfatherAuthHeader = createAuthHeader(credentials.userId, credentials.apiKey);
		const response = await fetch(`${BREWFATHER_API_BASE}/recipes/${recipeId}`, {
			method: 'GET',
			headers: {
				'Authorization': brewfatherAuthHeader
			}
		});
		if (!response.ok) {
			if (response.status === 401) {
				throw new Error('Invalid Brewfather credentials.');
			}
			throw new Error(`Brewfather API error: ${response.status}`);
		}
		return await response.json();
	},

	getBatchLastReading: async (batchId: string, userId: string, authHeader: Record<string, string>): Promise<BrewfatherBatchReading | null> => {
		const credentials = await getBrewfatherCredentials(userId, authHeader);
		if (!credentials) {
			throw new Error('Brewfather credentials not configured.');
		}
		const brewfatherAuthHeader = createAuthHeader(credentials.userId, credentials.apiKey);
		const response = await fetch(`${BREWFATHER_API_BASE}/batches/${batchId}/readings/last`, {
			method: 'GET',
			headers: {
				'Authorization': brewfatherAuthHeader
			}
		});
		if (!response.ok) {
			if (response.status === 404) {
				// No readings available for this batch
				return null;
			}
			if (response.status === 401) {
				throw new Error('Invalid Brewfather credentials.');
			}
			throw new Error(`Brewfather API error: ${response.status}`);
		}
		return await response.json();
	},

	getBatchReadings: async (batchId: string, userId: string, authHeader: Record<string, string>, limit: number = 100): Promise<BrewfatherBatchReading[]> => {
		const credentials = await getBrewfatherCredentials(userId, authHeader);
		if (!credentials) {
			throw new Error('Brewfather credentials not configured.');
		}
		const brewfatherAuthHeader = createAuthHeader(credentials.userId, credentials.apiKey);
		const response = await fetch(`${BREWFATHER_API_BASE}/batches/${batchId}/readings?limit=${limit}`, {
			method: 'GET',
			headers: {
				'Authorization': brewfatherAuthHeader
			}
		});
		if (!response.ok) {
			if (response.status === 404) {
				// No readings available for this batch
				return [];
			}
			if (response.status === 401) {
				throw new Error('Invalid Brewfather credentials.');
			}
			throw new Error(`Brewfather API error: ${response.status}`);
		}
		const data = await response.json();
		return Array.isArray(data) ? data : [];
	}
};
